/* eslint-disable prefer-destructuring */
import { getBookingStatus } from "@Components/LeadFormModal/utils";
import { encryptDataString } from "helpers/encryption";
import { getLeaseDuration } from "@Pages/Bookings/utils/getLease";
import capitalizeFirstLetter from "@Utils/capitalizeFirstLetter";
import urlBuilder from "@Utils/urlBuilder";
import viewTransitionNavUtility from "@Utils/viewTransitionNavUtility";

// eslint-disable-next-line import/prefer-default-export
export const getApartmentConfigurationOptions = (country: string) => {
  let options: Array<any> = [];
  // eslint-disable-next-line default-case
  switch (country) {
    case "uk":
    case "canada":
    case "spain":
    case "ireland":
      options = [
        {
          label: "Ensuite Room",
          value: "Ensuite Room",
          description: "Private room & private bathroom, shared kitchen, shared living area",
        },
        {
          label: "Studio Room",
          value: "Studio Room",
          description: "Room, bathroom, kitchen, living area all private",
        },
        {
          label: "Shared Room",
          value: "Shared Room",
          description: "Room, bathroom, kitchen, living area all shared",
        },
        {
          label: "Private Room",
          value: "Private Room",
          description: "Room with shared bath and shared living area and kitchen",
        },
      ];
      break;
    case "us":
      options = [
        { label: "Studio Apartment", value: "Studio Apartment" },
        { label: "1BHK Apartment", value: "1BHK Apartment" },
        { label: "2BHK Apartment", value: "2BHK Apartment" },
        { label: "3BHK Apartment", value: "3BHK Apartment" },
      ];
      break;
    case "australia":
      options = [
        {
          label: "Ensuite Room",
          value: "Ensuite Room",
          description: "Private room & private bathroom, shared kitchen, shared living area",
        },
        {
          label: "Studio Room",
          value: "Studio Room",
          description: "Room, bathroom, kitchen, living area all private",
        },
        {
          label: "Shared Room",
          value: "Shared Room",
          description: "Room, bathroom, kitchen, living area all shared",
        },
        {
          label: "Private Room",
          value: "Private Room",
          description: "Room with shared bath and shared living area and kitchen",
        },
      ];
      break;
  }
  return options;
};

export const getLeaseDurationOptions = (country: string) => {
  const leaseDurationTypesUS = ["0 - 4 months", "5 - 8 months", "8+ months"];
  const leaseDurationTypesCommon = [
    "0 - 4 Weeks",
    "5 - 25 Weeks",
    "26 - 40 Weeks",
    "41 - 45 Weeks",
    "45+ Weeks",
  ];
  return country === "us" ? leaseDurationTypesUS : leaseDurationTypesCommon;
};

export const getTimeSlotOptions = () => {
  const timeSlots = [
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
    "17:30",
    "18:00",
  ];
  return timeSlots;
};

export const getRedirectURL = ({
  inventory,
  postSuccessData,
  formOneData,
  inModal,
  formTwoSavedData,
  withRedirectAnimation,
  bookFormSource,
  showSubmitConfirmation = false,
  isBookFormEnabledForInactiveProperty = false,
}: any) => {
  if (
    (getBookingStatus(inventory || inventory?.config) || isBookFormEnabledForInactiveProperty) &&
    inModal
  ) {
    return setFormUrl(inventory, postSuccessData, bookFormSource, showSubmitConfirmation);
  }
  if (inventory?.parent?.region?.canonical_name)
    return getSearchLink(inventory.parent.region.canonical_name);
  if (formOneData?.region?.elementValue?.canonical_name) {
    const isCustomUniversityAdded = localStorage.getItem("custom-university");
    const searchLocation =
      !isCustomUniversityAdded && formTwoSavedData.canonicalName
        ? formTwoSavedData.canonicalName
        : formOneData.region.elementValue.canonical_name;
    const utmSource = postSuccessData?.source_details?.utm_source;
    const utmCampaign = postSuccessData?.source_details?.utm_campaign;
    // const filters = getSearchPageFilters(formTwoSavedData);
    const filters = "";
    let redirectUrl = `${getSearchLink(searchLocation)}?`;
    if (withRedirectAnimation) redirectUrl = `${redirectUrl}${filters}&`;
    if (utmSource)
      redirectUrl = `${redirectUrl}utm_source=${utmSource}&utm_campaign=${utmCampaign}`;

    return redirectUrl;
  }
  return "/";
};

export const setFormUrl = (
  inventory: any,
  postSuccessData: any,
  bookFormSource: string,
  showSubmitConfirmation: boolean,
) => {
  const options: any = {};
  if (inventory.id) {
    // no parent id in case of root inventory and if parent id is present would be equal to its parent object id in case of config
    if (inventory.parent_id && inventory.parent_id !== inventory?.parent?.id) {
      // for tenancy -> with flexible move in date check
      options.inventory_id = inventory?.meta?.available_from_formatted
        ? inventory.id
        : `${inventory.config.id}${inventory.id}`;
    } else options.inventory_id = inventory.id; // root inventory case, contact us case
  }

  // this is for flexible config
  if (!inventory?.id && inventory.config && inventory?.config?.id)
    options.inventory_id = inventory?.config?.id;

  // resetMoveInDate flag comes from SectionChildrenOldV2(Contact us for different dates)
  // It means tenancy shouldn't be selected by default, so setting id of config(room)
  if (inventory.resetMoveInDate) {
    options.inventory_id = inventory?.id;
  }

  // set lead uuid
  if (postSuccessData?.uuid) {
    // Encrypt the lead_uuid for URL safety
    options.lead_uuid = encryptDataString(
      postSuccessData.uuid,
      "lead_user_lead_uuid",
      "lead_uuid",
    ).split("=")[1];
  }

  // Add config name
  if (postSuccessData?.data?.configuration)
    options.config_name = postSuccessData.data.configuration;

  // Add config name
  if (inventory?.config?.name) options.config_name = inventory.config.name;
  else if (inventory?.tenancyName) options.config_name = inventory?.tenancyName;
  else if (inventory?.level === "child") options.config_name = inventory.name;

  // Add university
  if (postSuccessData?.data?.university) options.university = postSuccessData.data.university;

  // Set lease duration
  if (postSuccessData?.data?.lease_duration)
    options.lease_duration = postSuccessData.data.lease_duration;

  // Set move in date
  if (postSuccessData?.data?.move_in_date) options.move_in_date = postSuccessData.data.move_in_date;

  if (!inventory.id || inventory?.parent_id === inventory?.parent?.id) {
    options.lease_duration = "flexible";
  } else if (inventory.parent_id && inventory?.meta?.lease_duration)
    options.lease_duration = getLeaseDuration(inventory.meta);

  // Set user email
  if (postSuccessData?.user_details?.email) options.email = postSuccessData.user_details.email;

  if (postSuccessData?.user_details?.phone_details?.country_code)
    options["phone[first]"] = postSuccessData.user_details.phone_details.country_code;

  if (postSuccessData?.user_details?.phone_details?.national)
    options["phone[last]"] = postSuccessData.user_details.phone_details.national;

  if (postSuccessData?.user_details?.name) {
    const fullName = postSuccessData.user_details.name.split(" ");
    // eslint-disable-next-line prefer-destructuring
    options["fullName[first]"] = fullName[0];
    fullName.shift();
    options["fullName[last]"] = fullName.join(" ");
  }
  if (bookFormSource) {
    options["bookform_source"] = bookFormSource;
  }
  // Room details attribution (P1D-5512): the bookform's events and API meta read it back.
  if (inventory?.room_details_section) {
    options["section_name"] = inventory.room_details_section;
  }
  if (showSubmitConfirmation) {
    options["showSubmitConfirmation"] = true;
  }
  return getFormFullUrl(inventory.canonical_name, options);
};

export const getFormFullUrl = (canonical_name: string, params = {}) => {
  const canonicalName = canonical_name;
  const paths = ["/places", canonicalName, "form"];
  // Build link
  const url = urlBuilder(paths, { params });
  return url;
};

// get payment Url for inventory
export const getPaymentFullUrl = (canonical_name: string, params = {}) => {
  const canonicalName = canonical_name;
  const path = `/places/${canonicalName}/payment`;
  return urlBuilder(path, { params });
};

// Build search link from location name and place id
export const getSearchLink = (canonical_name: string, filters = {}, regionType = null) => {
  const paths = [];
  if (regionType === "inventory") {
    paths.push("/places");
  } else {
    paths.push("/places/search");
  }
  paths.push(canonical_name);

  // add url options
  const url_options: any = {};
  url_options.path = paths;
  // url_options.params = getSearchLinkParams(filters);
  url_options.params = filters;

  // Build link
  return urlBuilder(url_options.path, { params: url_options.params });
};

export const openBookForm = ({ url, target, history, customWindowOpen }: any) => {
  if (target && customWindowOpen) customWindowOpen({ url, target });
  else if (history) viewTransitionNavUtility(() => history.push(url), "right-to-left");
};

export const getBudgetDurationAndCurrency = (country: string) => {
  const durationAndCurrency: any = {};
  // eslint-disable-next-line default-case
  switch (country) {
    case "us":
      durationAndCurrency.budget_currency = "dollar";
      durationAndCurrency.budget_symbol = "$";
      break;
    case "uk":
      durationAndCurrency.budget_currency = "pound";
      durationAndCurrency.budget_symbol = "£";
      break;
    case "ireland":
      durationAndCurrency.budget_currency = "euro";
      durationAndCurrency.budget_symbol = "€";
      break;
    case "canada":
      durationAndCurrency.budget_currency = "cad";
      durationAndCurrency.budget_symbol = "CA$";
      break;
    case "australia":
      durationAndCurrency.budget_currency = "aud";
      durationAndCurrency.budget_symbol = "AU$";
      break;
    case "spain":
      durationAndCurrency.budget_currency = "pound";
      durationAndCurrency.budget_symbol = "€";
  }
  durationAndCurrency.budget_duration = country === "us" ? "monthly" : "weekly";
  durationAndCurrency.budget_duration_to_display = country === "us" ? "month" : "week";
  return durationAndCurrency;
};

export const getLFTwoDefaultData = (filters = {}) => {
  const defaultValues: any = {};
  let finalFilters: any = {};

  if (Object.keys(filters).length > 0) {
    finalFilters = filters;
  } else if (typeof window !== "undefined") {
    // keeping this code for property page filters
    const urlParams = new URLSearchParams(window.location.search);
    finalFilters = {
      meta_min_lease_duration: urlParams.get("meta_min_lease_duration") || "",
      meta_max_lease_duration: urlParams.get("meta_max_lease_duration") || "",
      meta_types: urlParams.get("meta_types") || "",
      range_available_price: urlParams.get("range_available_price") || "",
    };
  }

  if (finalFilters?.budget) {
    const [, maxBudget] = finalFilters.budget;
    defaultValues["budget"] = +maxBudget;
  }

  if (finalFilters?.minLeaseDuration) {
    const [min_lease, duration] = finalFilters.minLeaseDuration.split(" ");
    if (finalFilters?.maxLeaseDuration) {
      const [max_lease] = finalFilters.maxLeaseDuration.split(" ");
      defaultValues["lease_duration"] = `${min_lease} - ${max_lease} ${capitalizeFirstLetter(
        duration,
      )}`;
    } else {
      defaultValues["lease_duration"] = `${min_lease - 1}+ ${capitalizeFirstLetter(duration)}`;
    }
  }

  if (finalFilters?.roomType) {
    const roomTypes = {
      "Ensuite Room": ["private_room", "private_bathroom", "shared_kitchen"],
      "Studio Room": ["private_room", "private_bathroom", "private_kitchen"],
      "Shared Room": ["shared_room", "shared_bathroom", "shared_kitchen"],
      "Private Room": ["private_room", "shared_bathroom", "shared_kitchen"],
    };

    if (finalFilters.roomType.length === 3) {
      for (const [key, value] of Object.entries(roomTypes)) {
        if (finalFilters.roomType.every((metaType: string) => value.includes(metaType))) {
          defaultValues["configuration"] = key;
        }
      }
    }
  }

  return defaultValues;
};

export const getFiltersApplied = (filters = {}) => {
  const defaultValues = getLFTwoDefaultData(filters);

  // @ts-ignore
  if (filters.university) {
    // @ts-ignore
    defaultValues["university"] = filters.university;
  }
  return defaultValues;
};

export const convertYYYYMMDD = (date: string) => {
  const newDate = date.split("/").reverse();
  return newDate.join("/");
};

export const addPad = (day: number) => {
  if (day < 10) {
    return `0${day}`;
  }
  return day;
};

export const addDurationToDate = (
  date: any,
  durationVal: any,
  durationUnit: any,
  excludeEndDay = true,
) => {
  try {
    const newDate = new Date(date);

    const normalizedUnit = durationUnit?.toString().toLowerCase() || "";

    if (normalizedUnit.includes("week")) {
      newDate.setDate(newDate.getDate() + durationVal * 7);
    } else if (normalizedUnit.includes("month")) {
      const originalDay = newDate.getDate();
      newDate.setDate(1);
      newDate.setMonth(newDate.getMonth() + durationVal);

      const lastDayOfMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();

      newDate.setDate(Math.min(originalDay, lastDayOfMonth));
    }

    if (excludeEndDay) {
      newDate.setDate(newDate.getDate() - 1);
    }

    return newDate;
  } catch (error) {
    return new Date(date);
  }
};
